import React from 'react';
import styled from '@emotion/styled';
import { ApolloQueryResult } from '@apollo/client';
import { Text } from '@atlaskit/primitives';
import ToolTip from '@atlaskit/tooltip';
import EditIcon from '@atlaskit/icon/glyph/edit';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { OpenSidebarButton, useSidebar } from '@css-xp/base';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { EditShipToPartyContentEnum } from '@css-xp/ppc-ship-to-party-sidebar';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';
import { SIDEBAR } from './customer-address-detail-page-actions';
import { SoldToAddressDisabledTooltip } from './sold-to-address-disable-tooltip';

const ActionItemStyles = styled.div`
  padding: 0px 8px;
`;

interface EditAddressProps {
  transactionAccountId: string;
  shipToAddress: CommerceAddress;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  onCompleteDeactivation?: (_responsePayload?: any) => void;
  showDeactivateOption?: boolean;
  isNonCommercialPricingEligibility?: boolean;
  isPrimaryContactsEnabled?: boolean;
}

export const EditAddress = ({
  transactionAccountId,
  shipToAddress,
  refetch,
  onCompleteDeactivation,
  showDeactivateOption = false,
  isNonCommercialPricingEligibility = false,
  isPrimaryContactsEnabled = false,
}: EditAddressProps) => {
  const doesCustomerAdvocateHasAccess = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.UPDATE,
    resource: ADVOCATE_RESOURCE_TYPE.SHIP_TO_ADDRESS,
  });

  const { openSidebar } = useSidebar();

  if (!doesCustomerAdvocateHasAccess) {
    return null;
  }

  if (isNonCommercialPricingEligibility && !showDeactivateOption) {
    return <SoldToAddressDisabledTooltip />;
  }

  if (!showDeactivateOption) {
    return (
      <OpenSidebarButton
        sidebar={SIDEBAR.EDIT_SOLD_TO_ADDRESS}
        payload={{
          transactionAccountId: transactionAccountId,
          refetch,
          shipToAddress,
          shouldHideHeading: true,
          isAddType: false,
          isNameReadOnly: true,
          shouldHideNotificationDescription: true,
          showLinkedBillingProfiles: true,
          contentString: {
            [EditShipToPartyContentEnum.SUBMIT_BUTTON]: 'Save',
            [EditShipToPartyContentEnum.BILLING_PROFILE_UPDATE_SUCCESS]: 'Customer updated',
            [EditShipToPartyContentEnum.ADDRESS_NAME_LABEL]: 'Customer',
          },
        }}
        sidebarOptions={{
          sidebarTitle: 'Edit details',
          shouldAddPaddingAfterTitle: false,
        }}
        appearance="subtle"
        testId="edit-sold-to-address-button"
        label="Edit Address"
        spacing="compact"
        iconBefore={
          <ToolTip testId="edit-details-tooltip" content="Edit details">
            <EditIcon label="Edit" size="medium" />
          </ToolTip>
        }
      />
    );
  }

  const handleEditButtonClick = () => {
    const openSidebarPayload = {
      sidebarType: SIDEBAR.EDIT_SOLD_TO_ADDRESS,
      payload: {
        transactionAccountId: transactionAccountId,
        refetch,
        shipToAddress,
        shouldHideHeading: true,
        isAddType: false,
        isNameReadOnly: true,
        shouldHideNotificationDescription: true,
        showLinkedBillingProfiles: true,
        contentString: {
          [EditShipToPartyContentEnum.SUBMIT_BUTTON]: 'Save',
          [EditShipToPartyContentEnum.BILLING_PROFILE_UPDATE_SUCCESS]: 'Customer updated',
          [EditShipToPartyContentEnum.ADDRESS_NAME_LABEL]: 'Customer',
        },
      },
      sidebarOptions: {
        sidebarTitle: 'Edit details',
        shouldAddPaddingAfterTitle: false,
      },
    };
    openSidebar(openSidebarPayload);
  };

  const handleDeactivateButtonClick = () => {
    const openSidebarPayload = {
      sidebarType: SIDEBAR.DEACTIVATE_SOLD_TO_ADDRESS,
      payload: {
        transactionAccountId: transactionAccountId,
        shipToId: shipToAddress.id,
        isPrimaryContactsEnabled,
        onComplete: onCompleteDeactivation,
      },
      sidebarOptions: {
        sidebarTitle: 'Deactivate sold-to address',
        shouldAddPaddingAfterTitle: false,
      },
    };
    openSidebar(openSidebarPayload);
  };

  return (
    <ActionItemStyles>
      <DropdownMenu trigger="Actions" label="Actions" shouldRenderToParent testId="edit-address-actions">
        <DropdownItemGroup>
          <DropdownItem
            onClick={() => handleEditButtonClick()}
            isDisabled={isNonCommercialPricingEligibility}
            elemAfter={isNonCommercialPricingEligibility && <SoldToAddressDisabledTooltip />}
            testId="edit-address-button"
          >
            Edit
          </DropdownItem>
          <DropdownItem onClick={() => handleDeactivateButtonClick()} testId="deactivate-address-button">
            <Text color="color.text.warning">Start deactivation</Text>
          </DropdownItem>
        </DropdownItemGroup>
      </DropdownMenu>
    </ActionItemStyles>
  );
};
